<template>
  <div>
    <attach-files-dialog
      :opened.sync="attachFilesDialogOpened"
      @upload-all="uploadAll"
    />

    <preview-document-dialog
      v-if="currentCompanyId"
      :opened.sync="previewDocumentDialogOpened"
      :previewType="previewType"
      :previewLoaded="previewLoaded"
      :documentPreviewData="documentPreviewData"
      :selectedMessageId="selectedMessageId"
      :selectedCompanyId="currentCompanyId"
      :selectedDocumentLink.sync="selectedDocumentLink"
      @refresh-conversation="refreshConversation"
    />

    <v-alert
      v-if="selectedConversation && selectedConversation.advisor_out_of_office"
      :value="true"
      outline
      block
      :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
      color="primary"
      class="text-xs-left mb-2"
      style="white-space: pre-line"
    >
      {{ selectedConversation.advisor_out_of_office_message }}
    </v-alert>

    <v-alert
      v-else-if="selectedConversation && isEmpty(selectedConversation.messages)"
      :value="true"
      outline
      block
      :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
      color="primary"
      class="text-xs-left mb-4 mt-4"
    >
      {{ $t('inbox.noMessages') }}
    </v-alert>

    <!--    <v-layout class="ma-4" v-if="loading">
      <v-flex xs12>
        <v-progress-circular indeterminate size="40" color="primary"></v-progress-circular>
      </v-flex>
    </v-layout>-->
    <DynamicScroller
      ref="messageContainer"
      :items="showingMessages"
      :min-item-size="115"
      class="scroller messages-wrapper pt-3"
    >
      <template v-slot="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :size-dependencies="[
            item.text,
            item.is_firm_user,
            item.text.includes('$$attached$$'),
          ]"
          :data-index="index"
          :data-active="active"
          class="message-container"
        >
          <!--          <div v-if="showDivider(index)" class="divider-block">
            <v-divider class="my-2" />
            <span class="date">{{formatDayTitle(item)}}</span>
          </div>-->

          <v-divider v-if="index !== 0" class="my-2" />
          <v-layout v-if="!item.is_log">
            <v-avatar
              :tile="true"
              :size="44"
              color="transparent"
              class="avatar"
              v-if="
                item.is_firm_user &&
                  (currentUser.assigned_advisor_avatar || currentFirm.thumb_url)
              "
            >
              <img
                :src="
                  currentUser.assigned_advisor_avatar
                    ? currentUser.assigned_advisor_avatar
                    : currentFirm.thumb_url
                "
                class="logo"
              />
            </v-avatar>

            <v-avatar
              :tile="true"
              :size="44"
              color="grey"
              class="avatar"
              v-else
            >
              <span class="white--text headline">{{
                item.user_name[0].toUpperCase()
              }}</span>
            </v-avatar>
            <v-layout ml-2 column class="text-xs-left">
              <!-- <div class="message-name">{{ item.user_name }}</div> -->
              <v-layout align-center
                ><b class="mr-2 ph-no-capture">{{ item.user_name }}</b
                ><v-chip class="font-weight-bold" small v-if="item.is_firm_user"
                  >{{ currentFirm.name }} Team</v-chip
                ></v-layout
              >
              <v-layout align-center>
                <div class="caption grey--text text--darken-1">
                  {{ moment(item.created_at).format('ddd, MMM D hh:mm A') }} ({{
                    moment(item.created_at).fromNow()
                  }})
                </div>

                <v-layout
                  align-center
                  v-if="item.unseen_user_ids.includes(currentUser.id)"
                  class="ml-2"
                >
                  <v-chip
                    small
                    color="error"
                    text-color="white"
                    v-if="item.tag === 'emergency'"
                    >EMERGENCY</v-chip
                  >
                  <v-chip
                    small
                    color="warning"
                    text-color="white"
                    v-else-if="item.tag === 'urgent'"
                    >URGENT</v-chip
                  >
                  <v-chip small color="primary" text-color="white" v-else
                    >New</v-chip
                  >
                </v-layout>
              </v-layout>
            </v-layout>
          </v-layout>

          <v-layout
            column
            align-start
            v-if="item.text.includes('$$attached$$')"
          >
            <v-layout
              column
              :class="`message-line__text message-line__text--advisor`"
            >
              <v-layout>
                <v-layout class="mt-3" align-center>
                  <span class="mr-2">Attached</span>

                  <v-tooltip
                    bottom
                    v-for="(documentLink, i) in getDocumentLinks(item)"
                    :key="i"
                  >
                    <v-chip
                      small
                      slot="activator"
                      class="clickable-chip"
                      @click="previewDocument(documentLink, item)"
                    >
                      <v-icon size="18">attach_file</v-icon
                      ><span
                        class="text-truncate"
                        :style="
                          $vuetify.breakpoint.smAndUp
                            ? 'max-width: 300px'
                            : 'max-width: 100px'
                        "
                        >{{ getDocumentName(documentLink) }}</span
                      >
                    </v-chip>
                    <span
                      >Click to preview
                      <span
                        >(already saved under 'Attachments' in Documents)</span
                      ></span
                    >
                  </v-tooltip>
                </v-layout>
              </v-layout>
            </v-layout>
          </v-layout>

          <v-layout
            column
            align-center
            class="darkgrey--text body-1 my-2 text-xs-center"
            v-else-if="item.is_log"
          >
            {{ moment(item.created_at).format('lll') }} - {{ item.user_name }}
            {{ item.text }}
          </v-layout>

          <div v-else class="message-text text-xs-left mt-3">
            <span class="preview ql-editor" v-html="item.text" />
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>

    <template v-if="!currentUser.readonly_access">
      <quill-editor
        id="new-message-editor"
        v-model="newMessage"
        :options="editorOption"
        :disabled="sending"
        class="ql-message-input default-input"
      />

      <div class="action-btn-container">
        <v-layout>
          <v-tooltip top v-if="!isAdvisor && messageTagsEnabled">
            <v-radio-group
              v-model="messageTag"
              class="py-3 px-2 mt-0"
              slot="activator"
              hide-details
              row
            >
              <v-radio label="Not Urgent" value="general" class="my-0" />
              <v-radio label="Urgent" value="urgent" class="my-0" />
              <v-radio label="Emergency" value="emergency" class="my-0" />
            </v-radio-group>
            <span>Please tag the message accordingly</span>
          </v-tooltip>
        </v-layout>

        <v-layout align-center>
          <v-btn
            class="primary text-capitalize"
            @click="sendMessage"
            :loading="sending"
          >
            <span>{{ $t('inbox.send') }}</span>
          </v-btn>

          <div>
            <v-btn
              class="mx-2 text-capitalize"
              @click="openImportDocumentsDialog()"
              flat
            >
              <v-icon small>attach_file</v-icon>
              {{ $t('inbox.attachFile') }}
            </v-btn>
          </div>
        </v-layout>
      </div>
    </template>
  </div>
</template>

<script>
import API from '@/api';
import { mapGetters, mapState } from 'vuex';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import AttachFilesDialog from '@/components/dialogs/AttachFilesDialog';
import PreviewDocumentDialog from '@/components/dialogs/PreviewDocumentDialog';
import { supportedExtensionsForPreview } from '@/constants/variables';
import firebase from 'firebase';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';

export default {
  components: {
    AttachFilesDialog,
    PreviewDocumentDialog,
  },

  props: {
    currentCompanyId: {
      type: Number,
    },

    currentCompany: {
      type: Object,
    },

    shouldFocusEditor: {
      type: Boolean,
    },
  },

  mixins: [FirmPreferenceMixins],

  data() {
    return {
      firstRender: false,
      pageSize: 5,
      currentPage: 1,
      supportedExtensionsForPreview,
      selectedConversation: null,
      messageTag: 'general',
      loading: false,
      newMessage: '',
      editorOption: {
        formats: ['bold', 'italic', 'underline', 'link', 'indent', 'list'],
        modules: {
          toolbar:
            window.innerWidth >= 768
              ? [
                  ['bold', 'italic', 'underline'],
                  [{ indent: '-1' }, { indent: '+1' }],
                  ['link'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                ]
              : [['bold', 'italic', 'underline']],
        },
        placeholder: this.$t('inbox.message'),
        bounds: '.ql-message-input',
      },
      sending: false,

      attachFilesDialogOpened: false,

      selectedMessageId: null,
      selectedDocumentLink: '',
      previewType: '',
      previewLoaded: false,
      documentPreviewData: null,
      previewDocumentDialogOpened: false,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
      isAdvisor: 'isAdvisor',
      myConversations: 'myConversations',
      defaultCompany: 'defaultCompany',
    }),

    ...mapState('companies', ['companies']),

    showingMessages() {
      if (this.selectedConversation) {
        return this.selectedConversation.messages.filter(
          message => !message.scheduled,
        );
      } else {
        return [];
      }
    },
  },

  methods: {
    isEmpty,
    moment,
    showDivider(idx) {
      return (
        idx === 0 ||
        !moment(this.showingMessages[idx].created_at).isSame(
          moment(this.showingMessages[idx - 1].created_at),
          'day',
        )
      );
    },
    formatDayTitle(message) {
      const messageMomentDate = moment(message.created_at);
      return messageMomentDate.format('ddd, MMM D');
    },
    async uploadAll(documentsToImport) {
      let path = '';
      this.sending = true;

      try {
        for (const documentToImport of documentsToImport) {
          const companyId = this.currentCompanyId;

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            companyId,
            path: '/Attachments',
            conversationId: this.selectedConversation.id,
          });

          if (response.data.error) {
            console.log(response.data.error);
          } else if (isEmpty(path)) {
            path = response.data.document_location;
          } else {
            path += '&!&'.concat(`${response.data.document_location}`);
          }
        }

        this.$store.dispatch('application/setSuccessNotification', {
          message: `You have successfully uploaded ${
            documentsToImport.length
          } document${documentsToImport.length > 1 ? 's' : ''}!`,
        });

        const response2 = await API.conversation.sendMessage({
          text: `$$attached$$${path}`,
          conversationId: this.selectedConversation.id,
          userId: this.currentCompanyId,
        });

        if (response2.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });
          console.log(response2.data.error);
        } else {
          await this.refreshConversation();
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Attachment unsuccessful. Please try again.',
        });
      } finally {
        this.sending = false;
      }
    },

    getDocumentLinks(message) {
      return message.text.split('$$')[2].split('&!&');
    },

    getDocumentName(link) {
      return link.split('/')[link.split('/').length - 1];
    },

    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },

    async previewDocument(documentLink, message) {
      this.selectedMessageId = message.id;
      this.previewLoaded = false;

      try {
        const response = await API.conversation.downloadAttachment(
          documentLink,
        );

        if (response.data.document) {
          this.previewDocumentDialogOpened = true;
          this.companies.find(c => c.name === documentLink.split('/')[2]);

          this.selectedDocumentLink = documentLink;
          this.previewType = this.endsWithAny(
            this.supportedExtensionsForPreview,
            this.getDocumentName(documentLink),
          );
          this.documentPreviewData = response.data.document.data.link;
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.previewLoaded = true;
      }
    },

    async sendMessage() {
      if (this.newMessage && !this.sending) {
        this.sending = true;

        const response = await API.conversation.sendMessage({
          text: this.newMessage
            .trim()
            .split('\n')
            .join('<br/>'),
          conversationId: this.selectedConversation.id,
          hasReminder: this.remindClient,
          messageTag: this.messageTag,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem.',
          });

          console.log(response.data.error);
        } else {
          await this.refreshConversation();

          this.sending = false;
          this.newMessage = '';
        }
      }
    },

    async openImportDocumentsDialog() {
      this.attachFilesDialogOpened = true;
    },
    scrollToBottom() {
      if (this.$refs.messageContainer) {
        this.$refs.messageContainer.scrollToBottom();
      }
    },
    async refreshConversation() {
      try {
        this.loading = true;
        const response = await API.conversation.getConversation(
          this.currentCompany.relevant_conversation_id,
        );
        this.selectedConversation = Object.assign({}, response.data);
        this.updateConversationAsSeen();
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }

      this.scrollToBottom();
    },

    subscribeToNewMessages() {
      const database = firebase.database();

      this.newMessagesRef = database.ref(
        `users/${this.currentUser.id}/newMessages`,
      );

      this.listeningToNewMessages = this.newMessagesRef.on(
        'value',
        async () => {
          console.log('trigger event');
          if (this.currentUser.id && !this.loading) {
            await this.refreshConversation();
          }
        },
      );
    },

    unsubscribeToNewMessages() {
      if (this.listeningToNewMessages && this.newMessagesRef) {
        this.newMessagesRef.off('value', this.listeningToNewMessages);
      }
    },

    focusEditor() {
      this.$nextTick(() => {
        setTimeout(() => {
          const editor = document.querySelector(
            '#new-message-editor .ql-editor',
          );
          editor.scrollIntoView();
          editor.focus();

          this.$emit('update:shouldFocusEditor', false);
        }, 100);
      });
    },

    async updateConversationAsSeen() {
      await API.conversation.updateConversationAsSeen(
        this.currentCompany.relevant_conversation_id,
      );
    },

    async reload() {
      if (
        !isEmpty(this.savedConversationPageMessage) ||
        !isEmpty(this.savedConversationDialogMessage)
      ) {
        if (this.conversationPage) {
          this.newMessage = this.savedConversationPageMessage;
          this.$store.dispatch('setSavedConversationPageMessage', '');
        } else {
          this.newMessage = this.savedConversationDialogMessage;
          this.$store.dispatch('setSavedConversationDialogMessage', '');
        }
      }

      await this.refreshConversation();
    },
  },
  async created() {
    this.subscribeToNewMessages();

    if (this.shouldFocusEditor) {
      this.focusEditor();
    }
  },
  beforeDestroy() {
    this.unsubscribeToNewMessages();
  },
  watch: {
    shouldFocusEditor: {
      async handler(value) {
        if (value === true && this.loading === false) {
          this.focusEditor();
        }
      },
    },
  },
};
</script>

<style scoped lang="postcss">
.divider-block {
  position: relative;
}

.divider-block .date {
  background-color: #f4f6fa;
  position: absolute;
  top: -10px;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  max-width: 100px;
}

.message-container {
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 8px;
  padding-bottom: 8px;
  background-color: white;
  overflow-wrap: break-word;
}

.messages-wrapper {
  height: calc(100vh - 475px);
  max-height: calc(100vh - 475px);
  overflow-x: hidden;
  overflow-y: auto;
}

.messages-wrapper p {
  margin-bottom: 5px;
}

.client-dashboard-wrapper.single-entity .messages-wrapper {
  height: calc(100vh - 425px);
  max-height: calc(100vh - 425px);
}

.message-text {
  font-size: 15px;
}

.message-name {
  font-size: 15px;
  font-weight: bold;
  line-height: 17px;
}

.ql-message-input {
  flex: auto;
  background-color: #fff;
  margin-top: 10px;

  & >>> .ql-toolbar.ql-snow {
    border: none;
    text-align: start;
    background-color: #ebf1f5;
    border-radius: 4px 4px 0px 0px;
    border: 1px solid #ccc;
  }

  & >>> .ql-container.ql-snow {
    border: none;
    font-size: 14px;
  }

  & >>> .ql-container .ql-editor {
    min-height: 80px;
    resize: vertical;
    border: 1px solid #ccc;
    border-top: none;
  }

  & >>> .ql-formats button {
    height: 28px;
    width: 32px;
  }
}
.clickable-chip {
  & >>> .v-chip__content {
    cursor: pointer !important;
  }
}

.action-btn-container {
  border-radius: 0px 0px 4px 4px;
  border: 1px solid #ccc;
  border-top: none;
  background-color: #ebf1f5;
}
</style>
